import React, { useMemo } from 'react'
import {
  Artifact,
  ArtifactType,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import {
  ControlCheckSourceTitle,
  ControlCheckEvidenceSourceTitle,
} from 'src/pages/TrusteroIntelligence/artifacts/AuditScan/AuditScanArtifact.components'
import { AuditScanArtifactJoinInfo } from 'src/pages/TrusteroIntelligence/artifacts/AuditScan/AuditScanArtifact.constants'
import { EvidenceShowTabBody } from 'src/pages/Evidence/evidence.styles'
import { EvidenceConfigType } from 'src/pages/Evidence/evidence.constants'

const CrossReferencedDataSources = ({
  artifact,
}: {
  artifact: Artifact
}): JSX.Element => {
  const crossReferencedData = artifact.getCrossReferencedDataArtifact()
  if (!crossReferencedData) {
    return <></>
  }

  return (
    <EvidenceShowTabBody>
      <ControlCheckEvidenceSourceTitle
        sourceId={crossReferencedData.getEvidenceInstanceId1()}
        joinInfo={AuditScanArtifactJoinInfo.LEFT}
      />
      <ControlCheckEvidenceSourceTitle
        sourceId={crossReferencedData.getEvidenceInstanceId2()}
        joinInfo={AuditScanArtifactJoinInfo.RIGHT}
      />
    </EvidenceShowTabBody>
  )
}

const TestTableSources = ({
  artifact,
}: {
  artifact: Artifact
}): JSX.Element => {
  const testTable = artifact.getTestTableArtifact()?.getTestTable()

  if (!testTable) {
    return <></>
  }

  return (
    <EvidenceShowTabBody>
      <ControlCheckSourceTitle
        sourceId={testTable.getSourceId()}
        sourceType={testTable.getSourceType()}
      />
    </EvidenceShowTabBody>
  )
}

const SampledEvidenceSource = ({
  artifact,
}: {
  artifact: Artifact
}): JSX.Element => {
  const sampledEvidence = artifact.getSampledEvidenceArtifact()
  if (!sampledEvidence) {
    return <></>
  }

  return (
    <EvidenceShowTabBody>
      <ControlCheckEvidenceSourceTitle
        sourceId={sampledEvidence.getEvidenceInstanceId()}
      />
    </EvidenceShowTabBody>
  )
}

const FilteredEvidenceSource = ({
  artifact,
}: {
  artifact: Artifact
}): JSX.Element => {
  const filteredEvidence = artifact.getFilteredEvidenceArtifact()
  if (!filteredEvidence) {
    return <></>
  }

  return (
    <EvidenceShowTabBody>
      <ControlCheckEvidenceSourceTitle
        sourceId={filteredEvidence.getEvidenceInstanceId()}
      />
    </EvidenceShowTabBody>
  )
}
export const ArtifactSourceBody = ({
  configs,
}: {
  configs: EvidenceConfigType[] | null
}): JSX.Element => {
  const artifact = useMemo(() => {
    const body = configs?.[0]?.body
    if (!body || !(body instanceof Uint8Array)) return null
    try {
      return Artifact.deserializeBinary(body)
    } catch {
      return null
    }
  }, [configs])

  if (!artifact) {
    return (
      <EvidenceShowTabBody>
        <div>Unable to load source information.</div>
      </EvidenceShowTabBody>
    )
  }

  const artifactType = artifact.getType()

  if (artifactType === ArtifactType.ARTIFACT_CROSS_REFERENCED_DATA) {
    return <CrossReferencedDataSources artifact={artifact} />
  }

  if (artifactType === ArtifactType.ARTIFACT_TEST_TABLE) {
    return <TestTableSources artifact={artifact} />
  }

  if (artifactType === ArtifactType.ARTIFACT_SAMPLED_EVIDENCE) {
    return <SampledEvidenceSource artifact={artifact} />
  }

  if (artifactType === ArtifactType.ARTIFACT_FILTERED_EVIDENCE) {
    return <FilteredEvidenceSource artifact={artifact} />
  }

  return (
    <EvidenceShowTabBody>
      <div>No source information available.</div>
    </EvidenceShowTabBody>
  )
}
